/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';
import { Keys, containsKey, removeKey } from '../../utils/UNSAFE_keys';
import { SelectionDetail } from '../../UNSAFE_Collection';
import { getIsSelectionPending } from '#utils/PRIVATE_textSelectionUtils';
import { SelectionBehavior } from './SwatchSelection.types';

const handleSelect = <K>(
  event: MouseEvent | KeyboardEvent,
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionBehavior: SelectionBehavior,
  isSelectionRequired: boolean,
  onChange?: (detail: SelectionDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (itemKey == null) {
    return;
  }

  const selected = containsKey(selectedKeys, itemKey);
  let keySet = selectedKeys;
  if (!selected) {
    // should only contains this item key
    keySet = { all: false, keys: new Set([itemKey]) };
  } else if ((event as KeyboardEvent).key !== 'Enter') {
    // no de-select for enter key
    if (
      selectionBehavior === 'toggle' ||
      event.ctrlKey ||
      event.metaKey ||
      (event as KeyboardEvent).key === ' '
    ) {
      // de-select only when ctrl/meta key is pressed or selectionBehavior is
      // explicitly set to toggle
      keySet = removeKey(selectedKeys, itemKey, isSelectionRequired);
    }
  }

  // only invoke callback if there is really a change
  // note onSelectionChange should never be null here
  if (selectedKeys != keySet && onChange) {
    onChange({ value: keySet, target: event.target });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};

/**
 * Hook for handling various selection mode.  Customized for color swatch support.
 * @param keyExtractor function to extract the key based on provided element
 * @param selectedKeys the currently selected keys
 * @param isSelectionRequired true if empty selected key is not allowed, false otherwise
 * @param selectionBehavior the selection behavior.  If the value is 'toggle', then the key is
 *                          added/removed from the current selected keys.  If the value is 'replace',
 *                          then the current selected keys are cleared first before the key is added
 *                          unless the ctrl/shift/meta key is pressed.
 * @param onChange callback function that is invoked when selection has changed.
 * @returns an event map that the consumer can register as event listeners
 */
export function useSwatchSelection<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  isSelectionRequired: boolean,
  selectionBehavior: SelectionBehavior,
  onChange?: (detail: SelectionDetail<K>) => void
) {
  const isTouchPointerRef = useRef<boolean>(false);

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (!getIsSelectionPending()) {
        handleSelect<K>(
          event,
          keyExtractor,
          selectedKeys,
          isTouchPointerRef.current ? 'toggle' : selectionBehavior,
          isSelectionRequired,
          onChange
        );
      }
    },
    [keyExtractor, selectedKeys, isSelectionRequired, selectionBehavior, onChange]
  );

  // general keyboard events listen to keyUp for a11y
  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // older browser that we don't support returns 'Spacebar' for space key
      if (event.key === ' ' || event.key === 'Enter') {
        handleSelect<K>(
          event,
          keyExtractor,
          selectedKeys,
          selectionBehavior,
          isSelectionRequired,
          onChange
        );
      }
    },
    [keyExtractor, selectedKeys, selectionBehavior, isSelectionRequired, onChange]
  );

  const selectionProps = onChange == null ? {} : { onClick, onKeyUp };
  return { selectionProps };
}
